/**
 * One automation's test send (TEST-EMAIL T3): the readiness read behind the
 * dialog, and the one send.
 *
 * ITS OWN FILE (Regel 48), like `useMoveInReview`: the template tab is already
 * the largest file of the Automation Center, and this is a different
 * responsibility — a request budget and a send lock — that only the test-send
 * dialog uses.
 *
 * READ WHEN THE DIALOG OPENS, AND ONLY THEN. Mounting costs nothing, opening
 * the template tab costs nothing; `begin()` — the reader opening the dialog —
 * costs exactly one GET, and opening it again reads again, because the
 * quota and the stored prerequisites may have changed in between. Nothing is
 * cached across openings and nothing is polled. A read that answers after the
 * dialog was closed, or after it was opened again, is dropped.
 *
 * ONE SEND AT A TIME, NEVER REPEATED BY ITSELF — PER AUTOMATION, PAGE-WIDE.
 * The send itself lives in `automationTestEmailSends`, keyed by the automation:
 * a remount of the template tab mid-send (a phone turned, a browser Back) finds
 * it still running, shows its answer when it arrives, and cannot start a second
 * one. `send()` refuses while one is on its way — the store marks it pending
 * synchronously, before anything is awaited, so a second call in the same tick
 * is refused too; a failure is handed back as it is — no retry here or above. Every attempt the server accepts counts against the organisation's
 * daily limit, and an answer that never arrived may have been a sent e-mail.
 *
 * `busy` is "a send for this automation is on its way"; `mine` is "the send
 * shown is the one this instance started" (on its way or answered); `locked`
 * is both — only then does its dialog refuse to close.
 *
 * NOTHING IS DECIDED HERE. What a readiness or a failure means in words lives
 * in `automationTestEmailModel`; this hook only holds the server's answers.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import {
  fetchAdminAutomationTestEmailReadiness,
  sendAdminAutomationTestEmail,
} from "../../../api/adminData/automations";
import {
  acknowledgeTestEmailSend,
  beginTestEmailOpening,
  clearTestEmailRefusal,
  getTestEmailSend,
  startTestEmailSend,
  subscribeTestEmailSends,
  testEmailDialogClosed,
  testEmailDialogOpened,
} from "./automationTestEmailSends";

const IDLE_READINESS = { status: "idle", data: null, error: null };

export default function useAutomationTestEmail(automationKey) {
  // `idle` | `loading` | `ready` | `error`
  const [readiness, setReadiness] = useState(IDLE_READINESS);
  // `idle` | `pending` | `sent` | `failed`, shared by every instance for this key.
  const entry = useSyncExternalStore(subscribeTestEmailSends, () =>
    getTestEmailSend(automationKey)
  );
  const self = useRef(null);
  if (self.current === null) self.current = {};
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadReadiness = useCallback(async () => {
    seq.current += 1;
    const mine = seq.current;
    setReadiness({ status: "loading", data: null, error: null });
    try {
      const data = await fetchAdminAutomationTestEmailReadiness(automationKey);
      if (alive.current && mine === seq.current) {
        setReadiness({ status: "ready", data, error: null });
      }
    } catch (error) {
      if (alive.current && mine === seq.current) {
        setReadiness({ status: "error", data: null, error });
      }
    }
  }, [automationKey]);

  /** The reader opened the dialog: a fresh read, no answer already seen. */
  const begin = useCallback(() => {
    beginTestEmailOpening(automationKey);
    loadReadiness();
  }, [automationKey, loadReadiness]);

  /** The dialog closed: a read still on its way is dropped. */
  const end = useCallback(() => {
    seq.current += 1;
  }, []);

  /** A refusal that a new choice answers (a scenario) is taken back. */
  const clearAttempt = useCallback(() => {
    clearTestEmailRefusal(automationKey);
  }, [automationKey]);

  /** The open dialog for this key — so an answer arriving now is shown in it. */
  const dialogOpened = useCallback(() => testEmailDialogOpened(automationKey), [automationKey]);
  const dialogClosed = useCallback(
    (byReader) => testEmailDialogClosed(automationKey, { byReader }),
    [automationKey]
  );
  const acknowledge = useCallback(() => acknowledgeTestEmailSend(automationKey), [automationKey]);

  /**
   * Send once. Resolves to `{ ok: true, result }` or `{ ok: false, error }`, or
   * `null` when a send for this automation is already on its way. `report`
   * says the answer if no dialog can.
   */
  const send = useCallback(
    (scenarioKey, report) =>
      startTestEmailSend(automationKey, {
        owner: self.current,
        report,
        run: () =>
          sendAdminAutomationTestEmail(
            automationKey,
            scenarioKey ? { scenario_key: scenarioKey } : {}
          ),
      }),
    [automationKey]
  );

  const busy = entry.status === "pending";
  const mine = entry.owner === self.current;
  return {
    readiness,
    attempt: { status: entry.status, result: entry.result, error: entry.error },
    busy,
    mine,
    locked: busy && mine,
    begin,
    end,
    retryReadiness: loadReadiness,
    clearAttempt,
    dialogOpened,
    dialogClosed,
    acknowledge,
    send,
  };
}
